import { dbTrue, type ZohoLedgerDTO, type ZohoTaxDTO } from "../data/zohoRecordsApi";

// Reading Zoho's TDS and TCS rates the way an accountant would.

/**
 * Section codes Zoho stores one way and displays another.
 *
 * `206C_1H_6CR` is the API's key; Zoho's own Income TCS Rates screen shows
 * `206C(1H)`. The trailing `6CR` is the six-crore turnover threshold, which
 * the display drops. Extend this as new ones turn up rather than trying to
 * parse the pattern: the keys are Zoho's, not a scheme.
 */
const SECTION_LABEL: Record<string, string> = {
  "206C_1H_6CR": "206C(1H)",
};

/**
 * The section as a CA would write it.
 *
 * Three shapes turn up. Old Income Tax Act sections are already readable
 * ("194H", "194"). TCS keys carry a threshold suffix and have a display form.
 * The Income Tax Act 2025 renamed sections to descriptive keys
 * ("payment_to_non_union_employee"), which only need the underscores taking
 * out. Anything unrecognised comes back as itself rather than mangled.
 */
export function sectionLabel(code: string | null): string {
  if (code === null || code.trim() === "") return "—";
  if (SECTION_LABEL[code]) return SECTION_LABEL[code];
  if (/^\d+[A-Z]{0,3}$/.test(code)) return code;

  const words = code.replace(/_/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "2", not "2.0000". Rates are written the short way on every Indian form. */
export function ratePercent(value: string | number | null): string {
  const numeric = typeof value === "number" ? value : Number.parseFloat(value ?? "0");
  if (Number.isNaN(numeric)) return "0";
  return String(Number.parseFloat(numeric.toFixed(4)));
}

/**
 * Is this rate usable for a transaction on this date?
 *
 * Zoho decides by comparing the transaction date against the rate's own
 * applicable period, not by the `status` word — proven on a bill form, where
 * changing only the date swapped which rates the TDS dropdown offered. So this
 * mirrors that rule rather than reading `status`, which only ever answers for
 * today.
 */
export function coversDate(rate: ZohoTaxDTO, date: string): boolean {
  const from = rate.start_date;
  const to = rate.end_date;
  if (from !== null && from !== "" && date < from) return false;
  if (to !== null && to !== "" && date > to) return false;
  return true;
}

/**
 * The three things a rate can be, which is what Zoho's own filter offers:
 * All / Active / Inactive / Expired.
 *
 * Inactive is not a slower expired. An inactive rate is inside its dates and
 * still cannot be used, because somebody switched it off in Zoho; an expired
 * one is switched on and out of date. A CA reading this list needs to tell
 * them apart, because the fix is different — one is a click in Zoho, the other
 * is a new rate.
 *
 * Checked in that order: a rate that is both switched off and out of date is
 * reported as inactive, because that is the deliberate act.
 */
export type RateState = "active" | "inactive" | "expired";

export function rateState(rate: ZohoTaxDTO, date: string): RateState {
  if (dbTrue(rate.is_inactive)) return "inactive";
  return coversDate(rate, date) ? "active" : "expired";
}

export const RATE_STATE_LABEL: Record<RateState, string> = {
  active: "Active",
  inactive: "Inactive",
  expired: "Expired",
};

export interface RateGroups {
  /** Usable for a transaction dated today. */
  active: ZohoTaxDTO[];
  /**
   * Everything else: out of date, or switched off in Zoho. Grouped together
   * only so the usable ones sort above them — each row still says which it is,
   * because an expired rate is still what a back-dated entry inside its own
   * period will be given, and an inactive one is not.
   */
  historic: ZohoTaxDTO[];
}

export function groupByCover(rates: ZohoTaxDTO[], today: string): RateGroups {
  return {
    active: rates.filter((rate) => rateState(rate, today) === "active"),
    historic: rates.filter((rate) => rateState(rate, today) !== "active"),
  };
}

/** The two direct taxes this screen deals in. */
export type TaxKind = "tds" | "tcs";

/** A payload field that holds a ledger id, on either tax. */
export type LedgerSlot = "tds_payable_account_id" | "tax_account_id" | "tcs_receivable_account_id";

export interface LedgerSide {
  /** What Zoho calls the field on the wire. */
  field: LedgerSlot;
  /** The account type Zoho insists on there. */
  accountType: string;
  /** What Zoho names the account it uses when we name none. */
  zohoDefaultName: string;
}

/**
 * Which ledger each side of a rate posts to — THE ONE PLACE THIS IS DECIDED.
 *
 * ZOHO'S FIELD NAMES MEAN OPPOSITE SIDES FOR THE TWO TAXES. `tax_account_id`
 * appears on both, as the RECEIVABLE for TDS and the PAYABLE for TCS, against a
 * different account type each time. Both were read off Zoho's own dialogs on
 * organisation 60081360231 and confirmed against what came back on the rate.
 *
 * Everything else derives from this table and takes it as an argument rather
 * than re-deciding from the tax's name, so there is no second place the pairing
 * could be got wrong. Mirrored by `TdsRateCreator::LEDGER_TYPES` and
 * `TcsRateCreator::LEDGER_TYPES` on the server, which refuse on the same terms.
 */
export const TAX_LEDGERS: Record<TaxKind, { payable: LedgerSide; receivable: LedgerSide }> = {
  tds: {
    payable: {
      field: "tds_payable_account_id",
      accountType: "other_current_liability",
      zohoDefaultName: "TDS Payable",
    },
    receivable: {
      field: "tax_account_id",
      accountType: "other_current_asset",
      zohoDefaultName: "TDS Receivable",
    },
  },
  tcs: {
    payable: {
      field: "tax_account_id",
      accountType: "other_current_liability",
      zohoDefaultName: "TCS Payable",
    },
    receivable: {
      field: "tcs_receivable_account_id",
      accountType: "other_current_asset",
      zohoDefaultName: "TCS Receivable",
    },
  },
};

/** One tax's pairing of payload field to the account type Zoho wants there. */
export type LedgerSlots = Readonly<Partial<Record<LedgerSlot, string>>>;

export function ledgerSlots(kind: TaxKind): LedgerSlots {
  const { payable, receivable } = TAX_LEDGERS[kind];
  return {
    [payable.field]: payable.accountType,
    [receivable.field]: receivable.accountType,
  };
}

/**
 * The accounts to offer for one of those slots.
 *
 * ZOHO'S ALLOWLIST IS NARROWER THAN THE ACCOUNT TYPE, and refuses the rest with
 * a 400 reading "Kindly associate Other Current Liability account" while you
 * are holding one. Output SGST and Unearned Revenue are both other current
 * liabilities and both refused; Advance Tax and Prepaid Expenses are both other
 * current assets and both accepted. Proven by editing a rate on organisation
 * 60081360231.
 *
 * The list cannot be computed. Advance Tax (accepted) and Employee Advance
 * (refused) come back from the chart of accounts with identical values on every
 * boolean Zoho exposes. So this reproduces what Zoho's own dialog offers by the
 * one rule that matches it: anything not system-owned, plus anything an
 * existing rate already points at.
 *
 * The second clause matters. It is the only way an account like Advance Tax —
 * system-owned and yet accepted — can be offered at all, and it widens itself
 * as soon as there is evidence from Zoho rather than from a guess.
 *
 * Confirmed against the TCS dialog too, on an organisation with no TCS rates
 * at all: it offered exactly the non-system accounts of each type and nothing
 * more, which is this rule with an empty evidence set.
 *
 * Kept beside the backend rule in `DirectTaxRateCreator::requireLedger`, which
 * refuses on the same terms. If one moves, the other has to.
 *
 * The evidence is scoped to one tax's rates on purpose: a TDS rate pointing at
 * Advance Tax says nothing about whether Zoho would let a TCS rate do the same.
 */
export function ledgerChoices(
  ledgers: ZohoLedgerDTO[],
  rates: ZohoTaxDTO[],
  slots: LedgerSlots,
  slot: LedgerSlot
): ZohoLedgerDTO[] {
  const inUse = new Set<string>();
  for (const rate of rates) {
    for (const field of Object.keys(slots) as LedgerSlot[]) {
      const id = rate[field];
      if (id !== null && id !== undefined && id !== "") inUse.add(id);
    }
  }

  return ledgers.filter(
    (ledger) =>
      ledger.account_type === slots[slot] &&
      dbTrue(ledger.is_active) &&
      (!dbTrue(ledger.is_system) || inUse.has(ledger.account_id))
  );
}

/**
 * Which account Zoho will use for a slot if we name none.
 *
 * THERE IS NO SETTING FOR THIS. Zoho's Income TDS Settings page holds only the
 * transaction-vs-line-item choice and the liabilities report switch;
 * `/settings/directtaxes` holds defaults for penalty and interest accounts and
 * nothing for payable or receivable. Zoho maps to the two accounts it seeded
 * the organisation with, named TDS Payable and TDS Receivable, and offers no
 * way to point that elsewhere — the per-rate override is the only lever.
 *
 * Since it cannot be read, it is inferred: the account the most existing rates
 * already point at. That is Zoho's own answer, repeated as many times as there
 * are rates, rather than a name match that would break the day somebody renames
 * the ledger. Undefined on a client with no rates yet, where there is no
 * evidence and guessing would be worse than saying nothing.
 *
 * Undefined is the normal answer for TCS, and not for want of trying: an
 * organisation that has never had a TCS rate has no TCS Payable or TCS
 * Receivable account either. Zoho makes both the first time one is saved.
 */
export function defaultLedgerFor(
  rates: ZohoTaxDTO[],
  ledgers: ZohoLedgerDTO[],
  slot: LedgerSlot
): ZohoLedgerDTO | undefined {
  const tally = new Map<string, number>();
  for (const rate of rates) {
    const id = rate[slot];
    if (id !== null && id !== undefined && id !== "") tally.set(id, (tally.get(id) ?? 0) + 1);
  }

  let best: string | undefined;
  let seen = 0;
  tally.forEach((count, id) => {
    if (count > seen) {
      best = id;
      seen = count;
    }
  });

  return best === undefined ? undefined : ledgers.find((ledger) => ledger.account_id === best);
}

/** Today as YYYY-MM-DD in local time, which is the shape Zoho's dates come in. */
export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}
